// =============================================
// musicLegal — Music service legal agreement + per-upload ownership consent
// =============================================
// Source-of-truth text for the two music legal documents, plus helpers to record
// and retrieve signatures. Every acceptance snapshots the EXACT text the user agreed
// to into mus_legal_acceptance (so the record stands even if the template changes).
//
// Two acceptance types:
//   'initial_agreement' — signed ONCE before using the music service (gate).
//   'upload_consent'    — signed on EVERY upload batch (ownership certification).
//
// The mus_legal_acceptance rows are the retrievable legal paperwork (surfaced in the
// Membership tab → "Your signed paperwork", and editable/viewable in Data & Backups).
// =============================================
import { loadTable, getTableData, addRow, saveTableData, checkApi } from '../features/db-viewer/data/dataAccess';

// Music API base (mirrors dbViewerService). The legal endpoints stamp the IP +
// user-agent SERVER-SIDE — the client can't be trusted to report its own IP.
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export interface LegalDoc {
  code: string;
  version: string;
  title: string;
  body: string;
}

export const SERVICE_AGREEMENT: LegalDoc = {
  code: 'music_service_agreement',
  version: '1.0',
  title: 'Akountant Music — Service & Ownership Agreement',
  body: `Akountant Music is your personal cloud locker for music you already own. It is
not a streaming service and not a marketplace — it is a place to keep the songs from
the CDs, vinyl, cassettes and digital purchases you have legally acquired, so you can
play them anywhere and never lose them.

By continuing, you agree to the following:

1. LEGAL OWNERSHIP. You will upload only music that you have legally purchased or that
   you otherwise have a lawful right to copy for personal use. You represent and warrant
   that you hold a valid licence or ownership for every file you upload.

2. PERSONAL USE ONLY. Your library is for your own personal, non-commercial use. You will
   not redistribute, broadcast, publicly perform, sell, or share your uploaded files, and
   you will not use Akountant Music to make copies for other people.

3. NO INFRINGING CONTENT. You will not upload music you obtained from unauthorized
   sources (e.g. pirated downloads, ripped-from-streaming, or files you do not have the
   right to copy). Uploading content you do not own is a breach of this agreement.

4. TAKEDOWN & REPEAT INFRINGERS. We comply with the DMCA and equivalent laws. We may
   remove content and suspend or terminate accounts in response to valid infringement
   notices. Repeat infringers will lose access to the service.

5. STORAGE LIMITS. Your plan sets how many songs and how many megabytes you may store,
   and whether video is allowed. If you exceed your limit, or downgrade to a smaller plan,
   you may be required to remove files before adding more.

6. RECORD-KEEPING & CONSENT TO STORE THIS AGREEMENT. You consent to us recording and
   retaining this signed agreement, and a separate ownership certification for every
   upload, including the date, your typed signature, and technical details such as your
   browser. These records are kept as legal documentation and may be produced if a
   question of ownership ever arises.

7. INDEMNIFICATION. You will indemnify and hold Akountant harmless from any claim arising
   out of files you upload in breach of this agreement.

8. TERMINATION. We may suspend or close the service, or your access to it, for breach of
   this agreement. You may delete your library at any time.

By typing your name and accepting below, you confirm you have read, understood, and agree
to this Agreement, and that you are the person entering into it.`,
};

export const UPLOAD_CONSENT: LegalDoc = {
  code: 'upload_ownership_consent',
  version: '1.0',
  title: 'Upload Ownership Certification',
  body: `Before adding these file(s) to your library, you must certify ownership.

I certify that:
• I legally own, or otherwise have the lawful right to copy for personal use, every file
  in this upload.
• These files were obtained from a legitimate source (purchased media, authorized digital
  purchase, or my own original recording).
• I am uploading them for my own personal, non-commercial use only, and I will not
  redistribute or share them.
• I understand that this certification, with the date, my signature, and technical details,
  is recorded and retained as a legal document, and that uploading content I do not own may
  result in removal of the content and termination of my account.

This certification is required for every upload and is bound to the specific file(s) in
this batch.`,
};

function nowTimestamp(): string {
  return new Date().toISOString().replace('T', ' ').substring(0, 19);
}

function makeBatchId(): string {
  return 'ub_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

function authToken(): string | null {
  return localStorage.getItem('akt_auth_token');
}

/**
 * POST an acceptance to the server (which stamps the IP + user-agent). Returns the
 * inserted row, or null if the API/token isn't available (caller falls back to a
 * local insert with no server IP).
 */
async function postAcceptanceToApi(payload: Record<string, any>): Promise<any | null> {
  const token = authToken();
  if (!token || !(await checkApi())) return null;
  try {
    const res = await fetch(`${API_BASE}/music/legal-acceptance`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(payload),
    });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

/** Append a server-returned acceptance row to the local cache so getAcceptances sees it. */
function cacheAcceptance(row: any): void {
  try {
    const data = getTableData('mus_legal_acceptance');
    data.push(row);
    saveTableData('mus_legal_acceptance', data);
  } catch { /* best-effort cache update */ }
}

/**
 * Warm the acceptance records into the cache (call on music entry). Prefers the
 * authenticated music API (so account owners get their server-persisted records
 * with server-captured IP); falls back to the localStorage table.
 */
export async function loadLegalRecords(): Promise<void> {
  const token = authToken();
  if (token && (await checkApi())) {
    try {
      const res = await fetch(`${API_BASE}/music/legal-acceptance`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { saveTableData('mus_legal_acceptance', await res.json()); return; }
    } catch { /* fall through to local */ }
  }
  await loadTable('mus_legal_acceptance');
}

/** Has the user signed the CURRENT initial service agreement? */
export function hasAcceptedInitialAgreement(userId: number | null): boolean {
  if (userId == null) return false;
  return getTableData('mus_legal_acceptance').some((a: any) =>
    // eslint-disable-next-line eqeqeq
    a.user_id == userId &&
    a.acceptance_type === 'initial_agreement' &&
    a.doc_code === SERVICE_AGREEMENT.code &&
    a.doc_version === SERVICE_AGREEMENT.version &&
    // eslint-disable-next-line eqeqeq
    a.is_deleted != 1
  );
}

function baseAcceptance(userId: number, doc: LegalDoc, signatureName: string) {
  return {
    user_id: userId,
    doc_code: doc.code,
    doc_version: doc.version,
    doc_title: doc.title,
    doc_body_snapshot: doc.body,
    signature_name: signatureName,
    accepted_at: nowTimestamp(),
    ip_address: null,                 // captured server-side via user_ip_log (future)
    user_agent: (typeof navigator !== 'undefined' ? navigator.userAgent : '').slice(0, 500),
    is_active: 1,
    is_deleted: 0,
    created_at: nowTimestamp(),
    updated_at: nowTimestamp(),
  };
}

/** Record the one-time initial service agreement (server-IP via API, else local). */
export async function recordInitialAgreement(userId: number, signatureName: string): Promise<void> {
  const row = await postAcceptanceToApi({
    acceptance_type: 'initial_agreement',
    doc_code: SERVICE_AGREEMENT.code,
    doc_version: SERVICE_AGREEMENT.version,
    doc_title: SERVICE_AGREEMENT.title,
    doc_body_snapshot: SERVICE_AGREEMENT.body,
    signature_name: signatureName,
    upload_batch_id: null,
    track_count: null,
  });
  if (row) { cacheAcceptance(row); return; }
  // Fallback (no API / localStorage mode): record locally, no server IP.
  await addRow('mus_legal_acceptance', {
    ...baseAcceptance(userId, SERVICE_AGREEMENT, signatureName),
    acceptance_type: 'initial_agreement',
    upload_batch_id: null,
    track_count: null,
  }, 'acceptance_id');
}

/**
 * Record a per-upload ownership consent and return the batch id, which the caller
 * stamps onto the uploaded tracks (so consent ↔ tracks are linked). The batch id is
 * generated client-side so it matches whether the record is stored via API or locally.
 */
export async function recordUploadConsent(userId: number, trackCount: number, signatureName: string): Promise<string> {
  const batchId = makeBatchId();
  const row = await postAcceptanceToApi({
    acceptance_type: 'upload_consent',
    doc_code: UPLOAD_CONSENT.code,
    doc_version: UPLOAD_CONSENT.version,
    doc_title: UPLOAD_CONSENT.title,
    doc_body_snapshot: UPLOAD_CONSENT.body,
    signature_name: signatureName,
    upload_batch_id: batchId,
    track_count: trackCount,
  });
  if (row) { cacheAcceptance(row); return batchId; }
  // Fallback (no API / localStorage mode): record locally, no server IP.
  await addRow('mus_legal_acceptance', {
    ...baseAcceptance(userId, UPLOAD_CONSENT, signatureName),
    acceptance_type: 'upload_consent',
    upload_batch_id: batchId,
    track_count: trackCount,
  }, 'acceptance_id');
  return batchId;
}

export interface AcceptanceRecord {
  acceptanceId: number;
  type: string;
  title: string;
  version: string;
  signatureName: string;
  acceptedAt: string;
  trackCount: number | null;
  batchId: string | null;
  body: string;
}

/** All of a user's signed paperwork, newest first (for the retrieval UI). */
export function getAcceptances(userId: number | null): AcceptanceRecord[] {
  if (userId == null) return [];
  return getTableData('mus_legal_acceptance')
    // eslint-disable-next-line eqeqeq
    .filter((a: any) => a.user_id == userId && a.is_deleted != 1)
    .map((a: any) => ({
      acceptanceId: Number(a.acceptance_id),
      type: a.acceptance_type,
      title: a.doc_title || a.doc_code,
      version: a.doc_version,
      signatureName: a.signature_name || '',
      acceptedAt: a.accepted_at || a.created_at || '',
      trackCount: a.track_count != null ? Number(a.track_count) : null,
      batchId: a.upload_batch_id || null,
      body: a.doc_body_snapshot || '',
    }))
    .sort((x, y) => (y.acceptedAt || '').localeCompare(x.acceptedAt || ''));
}
